import { updateCustomer, createConversationEvent } from '.';

/**
 * @param {string} customerId
 * @param {string} userId
 * @param {string} text
 * @param {any} actions
 * @param {Function} onSuccess
 * @param {Function} onFailure
 */
const sendConversationEvent = async (customerId: string, userId: string, text: string, actions: any, onSuccess: Function, onFailure: Function) => {
  await updateCustomer(customerId, {
    assignee_id: userId,
    conversation_status: 'responded',
  });

  const res = await createConversationEvent(customerId, "agent_message", text, actions);

  if (res && typeof res === 'object' && 'error' in res) {
    await onFailure();
  } else {
    await onSuccess();
  }
};

/**
 * @param {string} customerId
 * @param {string} text
 * @param {any} actions
 * @param {Function} onSuccess
 * @param {Function} onFailure
 */
const sendInternalNote = async (customerId: string, text: string, actions: any, onSuccess: Function, onFailure: Function) => {
  const res = await createConversationEvent(customerId, "internal_message", text, actions);

  if (res && typeof res === 'object' && 'error' in res) {
    onFailure();
  } else {
    onSuccess();
  }
};

/**
 * @param {string} eventType
 * @returns {string}
 */
const getChannel = (eventType: string): string => {
  switch (eventType) {
    case 'bot_message':
      return 'AI Team';
    case 'widget_message':
      return 'Live Chat';
    case 'gmail_message':
      return 'Gmail';
    case 'facebook_message':
      return 'Facebook';
    case 'instagram_message':
      return 'Instagram';
    case 'whatsapp_message':
      return 'Whatsapp';
    case 'email_message':
      return 'Email';
    case 'internal_message':
      return 'Internal Note';
    default:
      return '';
  }
};


/**
 * @param {any} customer
 * @returns {string}
 */
const getCustomerInitials = (customer: any): string => {
  if (customer.first_name && customer.last_name) {
    return customer.first_name[0] + customer.last_name[0];
  }

  if (customer.first_name) {
    const namePieces = customer.first_name.split(' ');
    if (namePieces.length === 1) {
      return customer.first_name.slice(0, 2);
    } else {
      return namePieces[0][0] + namePieces[1][0];
    }
  }

  if (customer.last_name) {
    const namePieces = customer.last_name.split(' ');
    if (namePieces.length === 1) {
      return customer.last_name.slice(0, 2);
    } else {
      return namePieces[0][0] + namePieces[1][0];
    }
  }

  if (customer.email) {
    return customer.email.slice(0, 2);
  }

  if (customer.city) {
    const namePieces = customer.city.split(' ');
    if (namePieces.length === 1) {
      return customer.city.slice(0, 2);
    } else {
      return namePieces[0][0] + namePieces[1][0];
    }
  }

  return 'CU';
};

/**
 * @param {string} text
 * @returns {string}
 */
const replaceWithAnchors = (text: string): string => {
  const urlRegex =
    /(\b(https?):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gi;

  text = text.replace(urlRegex, (url: string) => {
    return '<a href="' + url + '" target="_blank">' + url + "</a>";
  });

  text = text.replace(/(?:\r\n|\r|\n)/g, '<br>');

  return text;
};


// Returns array of objects with `type` and `value` keys.
// `type` is either 'match' or 'non-match'.
/**
 * @param {string} text
 * @returns {string[] | null}
 */
const findProductUrls = (text: string) => {
  const regex =
    /(\bhttps?:\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*\/products\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gi;

  return text.match(regex);
};

import type { ConversationEvent } from '../types/conversation';

interface MessagePayload {
  text?: string;
  subject?: string;
  language?: string;
  translations?: Record<string, string>;
  [key: string]: any;
}

/**
 * Gets the best text to display for a message, prioritizing translations based on account language
 * @param {MessagePayload} payload - The message payload containing text, language and translations
 * @param {string} accountLanguage - The current account language setting
 * @param {string} messageType - The type of message (e.g., "agent_message", "internal_message")
 * @returns {string | undefined} - The text to display (original or translated)
 */
const getDisplayText = (
  payload: MessagePayload,
  accountLanguage: string | null | undefined,
  messageType?: string
): string | undefined => {
  try {
    if (!payload) {
      return undefined;
    }

    // Show the subject if available (for emails)
    if (payload.subject) return payload.subject;

    // For internal messages, always show the original text
    if (messageType === "internal_message") {
      return payload.text || "";
    }

    // First check if the message is already in the agent's language
    if (payload.language === accountLanguage) {
      return payload.text || "";
    }

    // If not in agent's language, check if a translation exists for the agent's language
    if (
      payload.translations &&
      accountLanguage &&
      accountLanguage in payload.translations
    ) {
      return payload.translations[accountLanguage];
    }

    // If no translation available, fall back to the original text
    return payload.text || "";
  } catch (error) {
    console.error("Error in getDisplayText:", error);
    return "";
  }
};

/**
 * Check if translation is available for a message
 * @param {MessagePayload} payload - The message payload
 * @param {string} accountLanguage - The current account language
 * @param {string} messageType - The type of message
 * @returns {boolean} - Whether a translation is available and should be shown
 */
const translationAvailable = (
  payload: MessagePayload,
  accountLanguage: string | null | undefined,
  messageType?: string
): boolean => {
  // Check if payload and account language exist
  if (!payload || !accountLanguage) {
    return false;
  }

  // Don't show translations for internal notes
  if (messageType === "internal_message") {
    return false;
  }

  const diffLanguageThanAccount = payload.language !== accountLanguage;
  const acctTranslationAvailable =
    accountLanguage &&
    payload.translations &&
    accountLanguage in payload.translations;

  return Boolean(diffLanguageThanAccount && acctTranslationAvailable);
};


interface ProductLog {
  productUrl?: string;
}

interface TextLog {
  text?: string;
}

/**
 * Takes a string and splits out any product URLs from the text.
 * Returns an array of objects with ProductLog and TextLog types.
 * @param {string} text - The message sent
 */
const splitProductAndTextLogs = (
  text: string
): (ProductLog | TextLog)[] => {
  const productUrls = findProductUrls(text);

  const logs: (ProductLog | TextLog)[] = [];

  if (productUrls) {
    let textWithoutUrls = text;
    productUrls.forEach((url: string) => {
      textWithoutUrls = textWithoutUrls.replaceAll(url, "");
    });

    // Show text message first and product cards after
    logs.push({ text: textWithoutUrls });

    productUrls.forEach((url: string) => {
      logs.push({ productUrl: url });
    });
  } else {
    logs.push({ text });
  }

  return logs;
};

export {
  sendConversationEvent,
  sendInternalNote,
  getChannel,
  getCustomerInitials,
  replaceWithAnchors,
  findProductUrls,
  getDisplayText,
  translationAvailable,
  splitProductAndTextLogs,
};
